import React from "react";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {EntryOrExitModel} from "../../models/entryOrExit";
import {EntryOrExitService} from "../../services/entryOrExitService";
import {Api} from "../../../helpers/api";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import i18n from "../../../i18n/i18n";
import Loader from "../../../common/components/shared/loader/loader";
import ReloadButton from "../../../common/components/shared/reloadButton/reloadButton";
import Table from "../../../common/components/shared/table/table";
import {ReactComponent as AllowIcon} from "../../../statics/icons/icon_allow.svg";
import {ReactComponent as NotAllowIcon} from "../../../statics/icons/icon_discard.svg";
import "./entryOrExit.scss";
import {LocationType} from "../../../topology/models/location";
import AllowOrNotAllowPopup from "../../../common/components/shared/allowOrNotAllowPopup/allowOrNotAllowPopup";
import {ValidatedValue} from "../../../helpers/validatedValue";
import OpenOrCloseAllPopup from "./openOrCloseAllPopup/openOrCloseAllPopup";

interface EntryOrExitListState {
    entryOrExitData: EntryOrExitModel[] | undefined | null;
    entryOrExitResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    isTableLoader: boolean;
    displayPopup: "allowPopup" | "notAllowPopup" | "openAllPopup" | "closeAllPopup" | null;
    allowResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    notAllowResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    openCloseResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    selectedElement: EntryOrExitModel | undefined;
    isPopupActionWaiting: boolean;
}

export default class EntryOrExitList extends React.Component <any, EntryOrExitListState> {
    constructor(props: any) {
        super(props);

        this.state = {
            entryOrExitData: undefined,
            entryOrExitResponse: undefined,
            currentPage: 0,
            isTableLoader: false,
            displayPopup: null,
            allowResponse: undefined,
            notAllowResponse: undefined,
            openCloseResponse: undefined,
            selectedElement: undefined,
            isPopupActionWaiting: false
        };
    }

    get entryOrExitListData() {
        return this.state.entryOrExitData;
    }

    public async componentDidMount() {
        await this.getAllEntryOrExitList();
    }

    public render(): JSX.Element {
        return (
            <div className="entry-or-exit-list">
                {this.renderWrapperContent()}
                {(this.state.displayPopup === "allowPopup" || this.state.displayPopup === "notAllowPopup") && this.renderAllowOrNotAllowPopups()}
                {(this.state.displayPopup === "openAllPopup" || this.state.displayPopup === "closeAllPopup") && this.renderOpenOrCloseAllPopups()}
            </div>
        );
    }

    private renderWrapperContent() {
        if (!Api.isSuccess(this.state.entryOrExitResponse)) {
            return (
                <HttpError
                    response={this.state.entryOrExitResponse}
                    cause={i18n.t("place.entryOrExit.errors.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.entryOrExitListData === undefined) {
                return <Loader/>;
            } else {
                const properties = ["name", "description"];
                const headers = properties.map(key => i18n.t("place.entryOrExit.headers." + key));

                return (
                    <>
                        {this.renderHeader()}
                        <div className="entry-or-exit-list-content">
                            {this.entryOrExitListData && this.entryOrExitListData!.length && this.entryOrExitListData!.length > 0 ? (
                                this.renderSuccessContent(headers, properties, this.entryOrExitListData)
                            ) : (
                                <Table
                                    headers={headers}
                                    rows={[]}
                                    properties={properties}
                                    testId="entry-or-exit-table"
                                    currentPage={this.state.currentPage}
                                    numberOfElements={0}
                                    actionsLabel={ i18n.t("place.entryOrExit.headers.actions")}
                                />
                            )}
                        </div>
                    </>
                );
            }
        }
    }

    private renderActions(element: EntryOrExitModel): JSX.Element {
        const elementId = element.id;

        return (
            <>
                {elementId && (
                    <>
                        <span
                            className="action not-allow"
                            data-testid={`not-allow-button-${elementId}`}
                            onClick={() => {
                                this.setState({selectedElement: element});
                                this.onAllowOrNorAllowAction("notAllowPopup");
                            }}
                        >
                            {i18n.t("place.entryOrExit.actions.notAllow")}
                            <NotAllowIcon/>
                        </span>

                        <span
                            className="action allow"
                            data-testid={`allow-button-${elementId}`}
                            onClick={() => {
                                this.setState({selectedElement: element});
                                this.onAllowOrNorAllowAction("allowPopup");
                            }}
                        >
                            {i18n.t("place.entryOrExit.actions.allow")}
                            <AllowIcon/>
                        </span>
                        {
                            element.hasOpenedAll && (
                                <span
                                    className="action close-all"
                                    data-testid={`close-all-button-${elementId}`}
                                    onClick={() => {
                                        this.setState({selectedElement: element});
                                        this.onOpenOrCloseAllAction("closeAllPopup");
                                    }}
                                >
                                    {i18n.t("place.entryOrExit.actions.closeAll")}
                                </span>
                            )
                        }
                        {
                            !element.hasOpenedAll && (
                                <span
                                    className="action open-all"
                                    data-testid={`open-all-button-${elementId}`}
                                    onClick={() => {
                                        this.setState({selectedElement: element});
                                        this.onOpenOrCloseAllAction("openAllPopup");
                                    }}
                                >
                                    {i18n.t("place.entryOrExit.actions.openAll")}
                                </span>
                            )
                        }
                    </>
                )}
            </>
        );
    }

    private renderHeader() {
        return (
            <div className="header">
                <div className="actions">
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId="entry-or-exit-list-reload-btn"
                    />
                </div>
            </div>
        );
    }

    private renderSuccessContent(headers: string[], properties: string[], entryOrExitData: EntryOrExitModel[]) {
        return (
            <div>
                <Table
                    headers={headers}
                    rows={entryOrExitData}
                    properties={properties}
                    testId="entry-or-exit-table"
                    actions={this.renderActions.bind(this)}
                    pageable={true}
                    renderEmptyCell={true}
                    currentPage={this.state.currentPage}
                    numberOfElements={entryOrExitData.length}
                    isOldPagination
                    isTableLoader={this.state.isTableLoader}
                    actionsLabel={i18n.t("place.entryOrExit.headers.actions")}
                />
            </div>
        );
    }

    private renderAllowOrNotAllowPopups(){
        const isAllowPopup = this.state.displayPopup === "allowPopup";

        return (
            <AllowOrNotAllowPopup
                popupType={this.state.displayPopup}
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirmAllowOrNotAllow.bind(this, isAllowPopup)}
                locationName={this.state.selectedElement?.name || ""}
                isEntryLocationType={this.state.selectedElement?.locationType === LocationType.ENTRY_TERMINAL}
                response={isAllowPopup ? this.state.allowResponse : this.state.notAllowResponse}
                isOrderPopup={false}
                selectedLocation={new ValidatedValue(null)}
                isDownloadLocations={false}
                isWaiting={this.state.isPopupActionWaiting}
            />
        );
    }

    private renderOpenOrCloseAllPopups(){
        const isOpenPopup = this.state.displayPopup === "openAllPopup";

        return (
            <OpenOrCloseAllPopup
                popupType={this.state.displayPopup}
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirmOpenOrCloseAll.bind(this, isOpenPopup)}
                locationName={this.state.selectedElement?.name || ""}
                response={this.state.openCloseResponse}
                isWaiting={this.state.isPopupActionWaiting}
            />
        );
    }

    private onAllowOrNorAllowAction(displayPopup: "allowPopup" | "notAllowPopup" | null) {
        this.setState({displayPopup});
    }

    private onOpenOrCloseAllAction(displayPopup: "openAllPopup" | "closeAllPopup" | null) {
        this.setState({displayPopup});
    }

    private closePopup() {
        this.setState({
            displayPopup: null,
            allowResponse: undefined,
            notAllowResponse: undefined
        });
    }

    private async confirmAllowOrNotAllow(isAllow: boolean) {
        this.setState({
            allowResponse: undefined,
            notAllowResponse: undefined,
            entryOrExitResponse: undefined,
            isPopupActionWaiting: true
        });

        const response = await EntryOrExitService.allowOrNotAllowLocation(this.state.selectedElement!.id, isAllow);

        if (isAllow) {
            this.setState({allowResponse: response});
        } else {
            this.setState({notAllowResponse: response});
        }

        if (Api.isSuccess(response)) {
            const entryOrExitResponse = await EntryOrExitService.getAll();

            this.setState({
                entryOrExitData: entryOrExitResponse?.data?.data || null,
                entryOrExitResponse,
                selectedElement: undefined
            });

            this.closePopup();
        }

        this.setState({isPopupActionWaiting: false});
    }

    private async confirmOpenOrCloseAll(isOpen: boolean) {
        this.setState({
            openCloseResponse: undefined,
            entryOrExitResponse: undefined,
            isPopupActionWaiting: true
        });

        const response = await EntryOrExitService.openOrCloseAllLocation(this.state.selectedElement!.id, isOpen);

        this.setState({
            openCloseResponse: response
        });

        if (Api.isSuccess(response)) {
            const entryOrExitResponse = await EntryOrExitService.getAll();

            this.setState({
                entryOrExitData: entryOrExitResponse?.data?.data || null,
                entryOrExitResponse,
                selectedElement: undefined
            });

            this.closePopup();
        }

        this.setState({isPopupActionWaiting: false});
    }

    private async getAllEntryOrExitList() {
        this.setState({
            isTableLoader: true
        });

        const response = await EntryOrExitService.getAll();

        this.setState({
            entryOrExitData: response?.data?.data || null,
            entryOrExitResponse: response,
            currentPage: response?.data?.currentPage,
            isTableLoader: false
        });
    }

    private async reload() {
        this.setState({entryOrExitData: undefined});
        await this.getAllEntryOrExitList();
    }
}
